/**
 * In-service modification of a route sheet (BOE Fifth) — the passenger twin of
 * `ModifyDialog`.
 *
 * **One field at a time**, like the goods dialog: pick the field, see the current
 * value beside the new one, state a reason, and read the history row that will be
 * written before committing it.
 *
 * 🚨 **The two buttons are two different legal acts and must never be swapped.**
 *
 *  - **Guardar · mismo QR** → `POST /passenger-decas/{sheetId}/modifications`. The PDF is
 *    re-rendered onto **the same object key**, so the document number, the CSV, the URL
 *    and therefore the **QR the driver is already carrying** are all unchanged (Fifth.1).
 *    Previous values are retained and printed marked as no longer valid. The user stays
 *    on this sheet, because it is still this sheet.
 *  - **Emitir documento nuevo** → `POST /passenger-decas/{sheetId}/supersede`. A *different*
 *    route sheet with its own number, CSV, URL and QR; the original is retained and becomes
 *    `ANULADO / SUPERSEDED` (Fifth.2). The user is taken to the replacement, because the
 *    thing they must now deliver to the driver is the replacement.
 *
 * The parties are **not** amendable in place. `PassengerDecaModifiableFields` in the frozen
 * contract lists exactly four members and holds none of them — "changing the carrier or the
 * hirer is a different engagement and must go through `supersede`. Including one here returns
 * `422`." So `PASSENGER_MODIFIABLE_COLUMNS` is what the in-place button reads, and choosing a
 * party disables it and says why.
 *
 * ⚠️ **A service field *is* amendable in place**, which is not obvious from the list of four:
 * `'services'` names the whole per-service array rather than a column, and a service field
 * travels inside it. So the set is consulted only for **document-scope** targets.
 *
 * A service field is sent as the **whole replacement set** with that one value swapped: the
 * contract takes `services` entire and diffs it server-side to produce the history entries,
 * so a partial array would read as a deletion of the rest.
 *
 * 🚨 **A single-service sheet is offered no per-service driver or bus.** The three BOE Sixth
 * override columns mean "this service departs from the sheet", which needs a sheet to depart
 * from; with one service there is nothing, because the service *is* the sheet. Offering them
 * there would let one route sheet name two different drivers — both printed, since
 * `backend/src/pdf/render-passenger.ts` puts the override in the service's detail lines and
 * the sheet's own driver in section 4 — and here the contradiction lands on a document that is
 * **already issued**: Fifth.1 keeps the URL, the QR and the CSV, so the driver is carrying the
 * QR that fetches it. See `OVERRIDE_COLUMNS` and `targetsOf`. `PassengerCreateForm` holds the
 * same rule at the other end, in its `ServiceBlock` and `toInput`.
 *
 * A finished route sheet (#180) is amended in place only: the dialog offers only the columns
 * `PASSENGER_MODIFIABLE_COLUMNS` holds and no «Emitir documento nuevo», and says how the date returns it
 * to service.
 *
 * **Why this is a separate component rather than props on `ModifyDialog`.** The plan proposed
 * passing the dictionary and the modifiable set into the goods dialog. The dictionary is only
 * one of eight goods couplings there: it also walks `deca.consignments`, reads
 * `FieldDef.modifiableInPlace` and `FieldDef.control` (neither exists on `PassengerFieldDef`),
 * calls two `/decas/{decaId}/…` path literals that `openapi-fetch` types by literal,
 * invalidates the goods cache family, reads the 422 through `validationProblemOf` and renders
 * `ValidationSummary`, and navigates to `/decas/$decaId`. Parameterising all of that would
 * leave a component whose every behaviour is a prop. This follows what the branch already did
 * for the create form instead: two siblings over shared primitives.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  ifMatch,
  unwrap,
  type FieldError as ApiFieldError,
  type PassengerDeca,
  type PassengerDecaPatch,
  type PassengerFieldError,
  type PassengerServiceInput,
} from '@deca/api'

import { api } from '../app/api.ts'
import {
  apiErrorMessage,
  isConflict,
  passengerErrorsByField,
  passengerValidationProblemOf,
} from '../app/errors.ts'
import { useAuth } from '../app/auth.tsx'
import { useInvalidatePassengerDeca } from '../app/queries.ts'
import { canModify, isGenerated } from '../deca/estado.ts'
import {
  formatDateTime,
  joinServiceDate,
  localInputToRfc3339,
  SERVICE_DATE_BOUNDS,
  splitServiceDate,
} from '../deca/format.ts'
import {
  PASSENGER_FIELDS,
  PASSENGER_MODIFIABLE_COLUMNS,
  SERVICE_CLASSES,
  type PassengerFieldDef,
  type ServiceClassValue,
} from '../deca/passenger/fields.ts'
import { Button, Notice } from '../ui/primitives.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { PassengerValidationSummary } from '../ui/PassengerValidationSummary.tsx'
import { SelectInput, TextAreaInput, TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'

/** One amendable thing: a field of the sheet, or a field of one service. */
interface Target {
  key: string
  def: PassengerFieldDef
  /** 1-based, for a service field; absent for a sheet field. */
  seq?: number
  current: string
  /**
   * Amendable under Fifth.1 without a new document. A service field always is — it
   * rides inside `changes.services` — so this is the modifiable set's answer only for
   * the sheet's own fields.
   */
  modifiableInPlace: boolean
}

export function PassengerModifyDialog({
  sheet,
  etag,
  open,
  onClose,
}: {
  sheet: PassengerDeca
  etag: string | null
  open: boolean
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidatePassengerDeca()
  const { principal } = useAuth()

  // Not in service. The dialog opens only when `canAmend`, so that is FINALIZADO; and «Emitir
  // documento nuevo» is offered exactly when `canModify` holds, as `canAmend`'s docblock says (#180).
  const finished = !canModify(sheet)
  const hintId = useId()
  // A finished sheet is amended in place only (#180): what only a new document changes is «Duplicar»'s.
  const targets = useMemo(
    () => targetsOf(sheet).filter((candidate) => !finished || candidate.modifiableInPlace),
    [sheet, finished],
  )
  // Open on something amendable in place. Starting on a party field would put the
  // dialog into its one blocked state before the user has done anything. A finished sheet opens on
  // the date its hint names: its latest service's, the one the date rule reads (#180).
  const initial =
    (finished ? latestServiceDate(sheet, targets) : undefined) ??
    targets.find((candidate) => candidate.modifiableInPlace) ??
    targets[0]
  const [targetKey, setTargetKey] = useState(() => initial?.key ?? '')
  /**
   * Pre-filled with the current value, not blank. Blank would read as a change the
   * moment the dialog opened, and it would leave no way to say "clear this optional
   * field" as distinct from "I have not typed yet".
   */
  const [newValue, setNewValue] = useState(() => initial?.current ?? '')
  const [reason, setReason] = useState('')
  /** Said on the reason field, not in the notice at the dialog's head — the goods dialog's rule (#111's review). */
  const [reasonError, setReasonError] = useState<string | null>(null)
  const reasonField = useRef<HTMLTextAreaElement>(null)
  const [serverErrors, setServerErrors] = useState<Record<string, ApiFieldError>>({})
  const [fieldErrors, setFieldErrors] = useState<PassengerFieldError[] | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /** A refusal drawn at the dialog's head is brought into view — the goods dialog's rule (#134). */
  const refusalNotice = useRef<HTMLDivElement>(null)
  const refusalSummary = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (error) refusalNotice.current?.scrollIntoView?.({ block: 'nearest' })
  }, [error])
  useEffect(() => {
    if (fieldErrors) refusalSummary.current?.scrollIntoView?.({ block: 'nearest' })
  }, [fieldErrors])

  // Stamped when the dialog opens rather than read on every render: the preview is of
  // one row that will be written, not a running clock.
  const [openedAt] = useState(() => new Date().toISOString())

  const target = targets.find((candidate) => candidate.key === targetKey) ?? targets[0]
  const locked = target ? !target.modifiableInPlace : false
  const changed = Boolean(target) && newValue !== (target?.current ?? '')
  const fieldError = target ? serverErrors[errorKeyFor(target)]?.message : undefined

  function choose(key: string) {
    setTargetKey(key)
    // The new value belongs to the field it was typed for.
    setNewValue(targets.find((candidate) => candidate.key === key)?.current ?? '')
    setServerErrors({})
    setFieldErrors(undefined)
    setError(null)
  }

  async function submit(mode: 'inPlace' | 'supersede') {
    if (!target) return
    if (reason.trim().length < 3) {
      setReasonError(t('modify.reasonRequired'))
      // Focus brings the field into view, and on a phone opens the keyboard on it.
      reasonField.current?.focus()
      return
    }
    setBusy(true)
    setError(null)
    setServerErrors({})
    setFieldErrors(undefined)

    try {
      const changes = changesFor(sheet, target, newValue)
      if (mode === 'inPlace') {
        // Fifth.1 — the same sheet, re-rendered. No navigation and no new id: the URL,
        // the QR and the CSV are the ones already in the driver's hands.
        const result = await unwrap(
          api.POST('/api/v1/passenger-decas/{sheetId}/modifications', {
            params: { path: { sheetId: sheet.id }, header: ifMatch(etag) },
            body: { reason: reason.trim(), changes },
          }),
        )
        invalidate(result.id)
        toast.success(
          t(finished ? (result.estado === 'MODIFICADO' ? 'passengerModify.savedReturned' : 'passengerModify.savedStillFinished') : 'passengerModify.savedInPlace'),
        )
        onClose()
      } else {
        // Fifth.2 — a different document. Both sheets change: the original is retired.
        const result = await unwrap(
          api.POST('/api/v1/passenger-decas/{sheetId}/supersede', {
            params: { path: { sheetId: sheet.id }, header: ifMatch(etag) },
            body: { reason: reason.trim(), changes },
          }),
        )
        invalidate(sheet.id)
        invalidate(result.id)
        // Report what came back, not what was asked for. Since #108 the passenger `supersede`
        // issues the replacement, as its contract description always said it did, so this is
        // normally the `savedSupersede` branch. The draft branch is kept rather than deleted
        // because this reports rather than asserts: during a rollout the portal can briefly be
        // talking to the previous api container, and telling the user a document was issued when
        // a `BORRADOR` came back would send them to hand a driver something that does not exist.
        toast.success(
          isGenerated(result)
            ? t('passengerModify.savedSupersede', { documentNumber: result.documentNumber ?? '' })
            : t('passengerModify.savedSupersedeDraft'),
        )
        onClose()
        void navigate({ to: '/hojas-de-ruta/$sheetId', params: { sheetId: result.id } })
      }
    } catch (caught) {
      // 🚨 The passenger 422 is flat — `fieldErrors`, no parties, no penalty notice. The
      // goods `validationProblemOf` tests `Array.isArray(problem.partyErrors)` and answers
      // `null` for one of these, which would swallow every server message the API sent.
      const problem = passengerValidationProblemOf(caught)
      if (problem) {
        setServerErrors(passengerErrorsByField(problem))
        setFieldErrors(problem.fieldErrors)
      } else if (isConflict(caught)) {
        setError(t('passengerModify.versionConflict'))
      } else {
        setError(apiErrorMessage(caught, t))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('modify.title', { documentNumber: sheet.documentNumber ?? '' })}
      subtitle={t(finished ? 'modify.subtitleFinished' : 'modify.subtitle')}
      onClose={onClose}
      footer={
        <>
          {/* The alternative, stated beside the buttons rather than hidden behind the
              tab the user did not pick. */}
          {finished ? null : <p className="small muted dialog__footnote">{t('modify.alternative')}</p>}
          {/* In place first — same reasoning as `ModifyDialog`, and the two
              document types must not disagree about it. */}
          <Button
            variant="primary"
            icon="qr"
            loading={busy}
            disabled={busy || !changed || locked}
            title={locked ? t('passengerModify.partiesLocked') : undefined}
            onClick={() => void submit('inPlace')}
          >
            {t('modify.submitInPlace')}
          </Button>
          {finished ? null : (
            <Button
              icon="refresh"
              loading={busy}
              disabled={busy || !changed}
              onClick={() => void submit('supersede')}
            >
              {t('modify.submitSupersede')}
            </Button>
          )}
        </>
      }
    >
      <Notice tone="info">{t('modify.methodInPlaceHelp')}</Notice>
      {finished ? (
        // Read with the picker, which takes the focus when the dialog opens (#180).
        <div id={hintId}>
          <Notice tone="info">{t('passengerModify.finishedHint')}</Notice>
        </div>
      ) : null}

      {locked ? <Notice tone="warning">{t('passengerModify.partiesLocked')}</Notice> : null}
      {error ? (
        <div ref={refusalNotice}>
          <Notice tone="danger">{error}</Notice>
        </div>
      ) : null}
      {/* Not `compact`: the compact variant is the import review's, and it drops the
          server's own sentence — which here is the only place a failure about a field
          other than the one being edited could be read at all. The goods dialog renders
          its rail the same way. */}
      {fieldErrors ? (
        <div ref={refusalSummary}>
          <PassengerValidationSummary errors={fieldErrors} />
        </div>
      ) : null}

      <SelectInput
        label={t('modify.fieldToModify')}
        full
        value={targetKey}
        {...(finished ? { 'aria-describedby': hintId } : {})}
        onChange={(event) => choose(event.currentTarget.value)}
      >
        {targets.map((candidate) => (
          <option key={candidate.key} value={candidate.key}>
            {qualifiedLabel(candidate, t)}
          </option>
        ))}
      </SelectInput>

      {target ? (
        <div className="form-grid form-grid--pair">
          <TextInput
            label={t('modify.currentValue')}
            value={target.current || t('common.none')}
            readOnly
            disabled
          />
          <NewValueControl
            target={target}
            value={newValue}
            error={fieldError}
            onChange={setNewValue}
          />
        </div>
      ) : null}

      <TextAreaInput
        label={t('modify.reason')}
        help={t('modify.reasonHelp')}
        placeholder={t('passengerModify.reasonPlaceholder')}
        required
        rows={2}
        ref={reasonField}
        error={reasonError ?? undefined}
        value={reason}
        onChange={(event) => {
          setReason(event.currentTarget.value)
          setReasonError(null)
        }}
      />

      {/* What Fifth.1 will record, before it is recorded. */}
      <div className="history-preview">
        <span className="history-preview__label">{t('modify.willRecord')}</span>
        {changed && target ? (
          <span className="history-preview__row">
            <span className="tabular">{formatDateTime(openedAt, i18n.language)}</span>
            <span>·</span>
            <span>{qualifiedLabel(target, t)}</span>
            <span>·</span>
            <span>
              <s>{target.current || '—'}</s> → {newValue || '—'}
            </span>
            <span>·</span>
            <span>{principal?.fullName ?? principal?.email}</span>
          </span>
        ) : (
          <span className="small muted">{t('modify.previewEmpty')}</span>
        )}
      </div>

      {/* The supersede path's own consequence, stated where the button is rather than
          discovered on the replacement's screen. ↻ It used to say the replacement would arrive as
          a draft without a PDF or a QR, which stopped being true with #108 — and the toast after
          the submit then said the opposite of the warning above the button. */}
      {finished ? null : <Notice tone="warning">{t('passengerModify.supersedeIssuesNew')}</Notice>}

      <Notice tone="warning" title={t('create.deliverReminder')}>
        <span className="small">{t('modify.deliverInPlace')}</span>
      </Notice>
    </Dialog>
  )
}

/**
 * The control for the new value.
 *
 * 🚨 Two columns are special-cased for the reason `PassengerCreateForm`'s `ServiceBlock`
 * states: `servicio_fecha` is a date with an optional hour (#102) and `servicio_clase` is an enum, and
 * `PassengerFieldDef` deliberately carries no `control` member — adding one would make
 * `'control' in def` elsewhere narrow a passenger definition to the goods `FieldDef` with
 * no compile error, because that match is purely structural.
 */
function NewValueControl({
  target,
  value,
  error,
  onChange,
}: {
  target: Target
  value: string
  error?: string
  onChange: (next: string) => void
}) {
  const { t } = useTranslation()
  const label = t('modify.newValue')

  if (target.def.column === 'servicio_clase') {
    return (
      <SelectInput
        label={label}
        error={error}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        <option value="">{t('common.none')}</option>
        {SERVICE_CLASSES.map((clase) => (
          <option key={clase} value={clase}>
            {t(`serviceClass.${clase}`)}
          </option>
        ))}
      </SelectInput>
    )
  }

  // The service date, and the hour that is optional beside it (#102) — the goods dialog's two
  // controls, for its reason: `datetime-local` cannot be left half-empty, and empty is what "no
  // time of day" is. The value carried between them is `YYYY-MM-DD[THH:MM]`, the one shape
  // `changesFor` reads.
  if (target.def.column === 'servicio_fecha') {
    const date = value.slice(0, 10)
    const time = value.length > 10 ? value.slice(11, 16) : ''
    return (
      <div className="form-grid">
        <TextInput
          label={label}
          error={error}
          type="date"
          {...SERVICE_DATE_BOUNDS}
          value={date}
          onChange={(event) => onChange(joinServiceDate(event.currentTarget.value, time))}
        />
        <TextInput
          label={t('fields.servicio_fecha.hora')}
          help={t('fields.servicio_fecha.horaHelp')}
          type="time"
          value={time}
          onChange={(event) => onChange(joinServiceDate(date, event.currentTarget.value))}
        />
      </div>
    )
  }

  if (target.def.multiline) {
    return (
      <TextAreaInput
        label={label}
        error={error}
        rows={2}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    )
  }

  return (
    <TextInput
      label={label}
      error={error}
      type="text"
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  )
}

/** The `servicio_fecha` target of the sheet's latest service: the date the date rule reads (#180). */
function latestServiceDate(sheet: PassengerDeca, targets: readonly Target[]): Target | undefined {
  let latest: Target | undefined
  let latestAt = -Infinity
  for (const candidate of targets) {
    if (candidate.def.column !== 'servicio_fecha' || candidate.seq == null) continue
    const at = Date.parse(sheet.services[candidate.seq - 1]?.servicio_fecha ?? '')
    if (at > latestAt) {
      latest = candidate
      latestAt = at
    }
  }
  return latest
}

/** The label as the picker and the history preview name it — party qualified, service numbered. */
function qualifiedLabel(target: Target, t: TFunction): string {
  const label = t(`fields.${target.def.column}.label`)
  const qualified =
    target.def.section === 'transportista' || target.def.section === 'contratante'
      ? `${t(`passengerSections.${target.def.section}`)} · ${label}`
      : label
  return target.seq == null ? qualified : `${qualified} · ${t('passengerCreate.service', { seq: target.seq })}`
}

/**
 * Where a server error for this target lands — the key shape `passengerErrorsByField`
 * produces, which is `services.<seq>.<column>` for a service failure and the bare column
 * otherwise.
 *
 * The three override columns need no exception to that, and the gate in `targetsOf` is what
 * keeps it so: a bare `conductor_nif` names both the sheet's driver and a service's override,
 * and one key cannot address two targets — but a sheet that offers the override at all is a
 * grouped one, where the override target carries a `seq` and the sheet's own does not. A
 * `seq`-less override is not a case that arises. (`PassengerCreateForm`'s own `errorKeyFor`
 * reaches the same rule from the other side; the two are stated separately because the two
 * files share no module.)
 */
function errorKeyFor(target: Target): string {
  return target.seq == null ? target.def.column : `services.${target.seq}.${target.def.column}`
}

/**
 * The three columns BOE Sixth lets one service of a **grouped** sheet override — the sheet's
 * own driver and bus, restated for a service that departs from them.
 *
 * A sheet with a single service has nothing to depart from, so `targetsOf` offers them there
 * as the sheet's own fields and nowhere else. `PassengerCreateForm` keeps its own copy of this
 * list under the same rule; the two files are siblings over shared primitives, not one module.
 */
const OVERRIDE_COLUMNS: readonly string[] = [
  'conductor_nombre',
  'conductor_nif',
  'matricula_autobus',
]

/**
 * Every amendable thing on this sheet, in the dictionary's order: the document fields, then
 * each service's own fields, and on a **grouped** sheet each service's three BOE Sixth
 * overrides as well.
 *
 * The overrides are the three columns that exist on the sheet *and* per service. On a grouped
 * sheet they therefore appear twice — once as the sheet's own value, once per service — and
 * the `seq` is what tells the two apart everywhere downstream. On a single-service sheet they
 * appear once, as the sheet's own: see the 🚨 at the top of this file for why a second reading
 * of the same driver is not a target the user may pick.
 *
 * The gate reads `services.length`, the very array this function walks, rather than the
 * `isGrouped` flag beside it — the backend computes that flag as `services.length > 1`, so the
 * two state one fact, and the one that cannot drift out of step with the targets is the array
 * the targets are built from.
 */
function targetsOf(sheet: PassengerDeca): Target[] {
  const out: Target[] = []
  const grouped = sheet.services.length > 1

  for (const def of PASSENGER_FIELDS) {
    if (def.scope !== 'document') continue
    const raw = (sheet as unknown as Record<string, unknown>)[def.column]
    out.push({
      key: def.column,
      def,
      current: String(raw ?? ''),
      modifiableInPlace: PASSENGER_MODIFIABLE_COLUMNS.has(def.column),
    })
  }

  sheet.services.forEach((row, index) => {
    for (const def of PASSENGER_FIELDS) {
      const isOverride = OVERRIDE_COLUMNS.includes(def.column)
      // The six service-scoped columns are genuinely per-service and are targets whatever the
      // service count. The three overrides are not a scope but a departure from the sheet, and
      // on a single-service sheet there is no sheet to depart from — offering one would put a
      // second driver on a document whose QR is already in a driver's hands.
      if (def.scope !== 'service' && !isOverride) continue
      if (isOverride && !grouped) continue
      const raw = (row as unknown as Record<string, unknown>)[def.column]
      out.push({
        key: `${index}.${def.column}`,
        def,
        seq: index + 1,
        // A service with no hour opens on an empty hour box, not a `00:00` nobody typed (#102).
        current: def.column === 'servicio_fecha' ? serviceDateInput(row) : String(raw ?? ''),
        // A service field always rides inside `changes.services`, which **is** one of the
        // four modifiable members, so it is amendable in place whatever the column is.
        modifiableInPlace: PASSENGER_MODIFIABLE_COLUMNS.has('services'),
      })
    }
  })

  return out
}

/**
 * The request body for changing exactly this one field.
 *
 * A service field goes back as the **whole** `services` array with the one value replaced
 * — the contract diffs the set server-side, so sending only the edited row would read as
 * deleting the others.
 *
 * Typed `PassengerDecaPatch` because `supersede` accepts that wider shape (the parties
 * included, which is the whole point of superseding); the in-place call's narrower
 * `PassengerDecaModifiableFields` is satisfied structurally, and the in-place button is
 * disabled for anything outside it so the wider members never travel that way.
 */
function changesFor(sheet: PassengerDeca, target: Target, newValue: string): PassengerDecaPatch {
  if (target.seq == null) {
    return { [target.def.column]: newValue } as PassengerDecaPatch
  }

  const index = target.seq - 1
  return {
    services: sheet.services.map((row, position) => {
      const base = serviceInput(row)
      if (position !== index) return base
      if (target.def.column === 'servicio_fecha') {
        // The flag travels with the date (#102): a blank hour is the day's 00:00 and says so.
        return { ...base, servicio_fecha: localInputToRfc3339(newValue), servicioFechaHasTime: newValue.length > 10 }
      }
      const value =
        // Cleared back to blank. `null` for the two optional service columns, which is
            // how the contract spells "no value"; `''` for a mandatory one, which the server
            // must refuse rather than silently keep. An override reaches the second branch —
            // its `def` is the sheet's, where the column *is* mandatory — and that is
            // harmless: `resolvePassengerService`'s `norm` turns `''` into `null` on the way
            // in, so a cleared override stores as "this service uses the sheet's own driver
            // and bus" either way. It is only ever reached on a grouped sheet; `targetsOf`
            // offers no override target on a single-service one.
            newValue || (target.def.mandatory ? '' : null)
      return { ...base, [target.def.column]: value }
    }),
  }
}

/**
 * A service's date as the two controls carry it: `YYYY-MM-DD`, plus `THH:MM` only when it has an
 * hour — the flag decides, never the hour, since a dated service is its day's 00:00 (#102).
 */
function serviceDateInput(row: PassengerDeca['services'][number]): string {
  const { date, time } = splitServiceDate(row.servicio_fecha, row.servicioFechaHasTime)
  return joinServiceDate(date, time)
}

/**
 * One service as the contract wants it written back — `seq` is presentation and is not sent.
 *
 * The flag goes back with the date (#102). Every service's date is re-sent, because the contract
 * diffs `services` whole; the server keeps a re-sent date's flag when none comes with it, and this
 * does not lean on that.
 */
function serviceInput(row: PassengerDeca['services'][number]): PassengerServiceInput {
  return {
    servicio_origen: row.servicio_origen,
    servicio_destino: row.servicio_destino,
    servicio_fecha: row.servicio_fecha,
    servicioFechaHasTime: row.servicioFechaHasTime ?? true,
    servicio_clase: row.servicio_clase as ServiceClassValue,
    servicio_paradas: row.servicio_paradas ?? null,
    servicio_clase_ref: row.servicio_clase_ref ?? null,
    conductor_nombre: row.conductor_nombre ?? null,
    conductor_nif: row.conductor_nif ?? null,
    matricula_autobus: row.matricula_autobus ?? null,
  }
}
